<template>
  <div class="page" :class="pageSizeClass">
    <!-- 저널 제목 -->
    <div class="journal-title-section">
      <h1 class="journal-title">International Journal of Korean Applied Linguistics</h1>
    </div>

    <!-- Volume 정보 -->
    <div class="volume-section">
      <p class="volume-text">Volume {{ journal?.volume || '' }}</p>
    </div>

    <!-- Editor in Chief 섹션 -->
    <div class="editor-section">
      <h2 class="section-title">Editor in Chief</h2>
      <div class="bullet-point">●</div>
      <div class="member-list">
        <p
          v-for="(editor, index) in editorInChiefList"
          :key="'eic-' + index"
          class="member-item"
        >
          {{ editor }}
        </p>
      </div>
    </div>

    <!-- Editorial Board 섹션 -->
    <div class="editor-section">
      <h2 class="section-title">Editorial Board</h2>
      <div class="bullet-point">●</div>
      <div class="member-list">
        <p
          v-for="(member, index) in editorialBoardList"
          :key="'eb-' + index"
          class="member-item"
        >
          {{ member }}
        </p>
      </div>
    </div>

    <!-- 학회 정보 (하단) -->
    <div class="society-section">
      <p class="society-name-en">The International Society of Korean Applied Linguistics</p>
      <p class="society-name-ko">국제한국어응용언어학회</p>
    </div>
  </div>
</template>

<script>
/* eslint-disable no-console */
export default {
  name: "article-title-page",
  props: {
    group: {
      type: Object,
      required: false,
      default: () => {},
    },
    journal: {
      type: Object,
      required: false,
      default: () => {},
    },
    pageSize: {
      type: String,
      required: false,
      default: "A5",
    },
  },
  computed: {
    pageSizeClass() {
      return this.pageSize?.toLowerCase() || "a5";
    },
    titlePageInfo() {
      try {
        if (this.journal?.title_page_info) {
          return JSON.parse(this.journal.title_page_info);
        }
      } catch (e) {
        console.warn('title_page_info 파싱 실패', e);
      }
      return {};
    },
    editorInChiefList() {
      const data = this.titlePageInfo.editorial_board_chair || '';
      if (!data) return [];
      return data.split('\n').map(s => s.trim()).filter(Boolean);
    },
    editorialBoardList() {
      const data = this.titlePageInfo.editorial_board || '';
      if (!data) return [];
      return data.split('\n').map(s => s.trim()).filter(Boolean);
    },
  },
};
</script>

<style lang="scss" scoped>
$primary-font-color: #231815;
$secondary-font-color: #666666;
$background-color: #ffffff;

// A4 패딩 (Journal Standard)
$a4-vertical-padding: 70px;
$a4-horizontal-padding: 70px;

// A5 패딩 (Journal Standard)
$a5-vertical-padding: 50px;
$a5-padding-left: 35px; // Binding Outer (Even Page)
$a5-padding-right: 55px; // Binding Inner (Even Page)

$sans-font-family: "Sandoll GtNeoCond", "Noto Sans KR", sans-serif;

$a4-width: 794px;
$a4-height: 1122px;
$a5-width: 559px;
$a5-height: 794px;

.page {
  background-color: $background-color;
  position: relative;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  page-break-before: always;
  page-break-after: always;

  &.a4 {
    width: $a4-width;
    min-height: $a4-height;
    padding: $a4-vertical-padding $a4-horizontal-padding;

    .journal-title-section {
      margin-bottom: 40px;
      margin-top: 20px;
    }

    .journal-title {
      font-size: 22px;
    }

    .volume-section {
      margin-bottom: 80px;
    }

    .volume-text {
      font-size: 16px;
    }

    .editor-section {
      margin-bottom: 50px;
    }

    .section-title {
      font-size: 16px;
      margin-bottom: 20px;
    }

    .bullet-point {
      font-size: 8px;
      margin-bottom: 15px;
    }

    .member-list {
      gap: 6px;
    }

    .member-item {
      font-size: 13px;
    }

    .society-section {
      padding-top: 60px;
    }

    .society-name-en {
      font-size: 12px;
    }

    .society-name-ko {
      font-size: 14px;
    }
  }

  &.a5 {
    width: $a5-width;
    min-height: $a5-height;
    padding: $a5-vertical-padding $a5-padding-right $a5-vertical-padding $a5-padding-left;

    .journal-title-section {
      margin-bottom: 25px;
      margin-top: 10px;
    }

    .journal-title {
      font-size: 16px;
    }

    .volume-section {
      margin-bottom: 40px;
    }

    .volume-text {
      font-size: 12px;
    }

    .editor-section {
      margin-bottom: 30px;
    }

    .section-title {
      font-size: 12px;
      margin-bottom: 12px;
      letter-spacing: 1px;
    }

    .bullet-point {
      font-size: 6px;
      margin-bottom: 10px;
    }

    .member-list {
      gap: 4px;
    }

    .member-item {
      font-size: 10px;
    }

    .society-section {
      padding-top: 35px;
    }

    .society-name-en {
      font-size: 9px;
    }

    .society-name-ko {
      font-size: 11px;
    }
  }
}

.journal-title-section {
  text-align: center;
}

.journal-title {
  font-family: $sans-font-family;
  font-weight: 700;
  color: $primary-font-color;
  margin: 0;
  letter-spacing: 1px;
}

.volume-section {
  text-align: center;
}

.volume-text {
  font-family: $sans-font-family;
  font-weight: 400;
  color: $primary-font-color;
  margin: 0;
}

.editor-section {
  text-align: center;
}

.section-title {
  font-family: $sans-font-family;
  font-weight: 700;
  color: $primary-font-color;
  margin: 0;
  letter-spacing: 2px;
}

.bullet-point {
  color: $primary-font-color;
}

.member-list {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.member-item {
  font-family: $sans-font-family;
  font-weight: 400;
  color: $primary-font-color;
  margin: 0;
  line-height: 1.6;
}

.society-section {
  margin-top: auto;
  text-align: center;
  border-top: 1px solid $secondary-font-color;
}

.society-name-en {
  font-family: $sans-font-family;
  font-weight: 400;
  color: $primary-font-color;
  margin: 0 0 8px 0;
}

.society-name-ko {
  font-family: $sans-font-family;
  font-weight: 500;
  color: $primary-font-color;
  margin: 0;
}
</style>
